import React, { useCallback, useContext, useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useRole, RoleContext } from "../../contexts/RoleContext";
import { isBrigadeDomain, isHelpDomain } from "../../utils/domain";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import NotificationBell from "../Notifications/NotificationBell";
import LiveIndicator from "./LiveIndicator";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import api from "../../services/ApiService";
import ThemeToggle from "./ThemeToggle";
import websocketNotificationService from "../../services/WebsocketNotificationService";

const Header = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);

  // Organizations state
  const [orgList, setOrgList] = useState<any[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgSlug, setNewOrgSlug] = useState("");
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [editOrgOpen, setEditOrgOpen] = useState(false);
  const [editOrgName, setEditOrgName] = useState("");
  const [editOrgSlug, setEditOrgSlug] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingOrg, setDeletingOrg] = useState(false);
  const selectedOrgId = Number(localStorage.getItem("fsm_org_id") || 0);

  // Безопасное использование useRole (не бросает ошибку если нет провайдера)
  const roleContext = React.useContext(RoleContext);
  const currentRole = roleContext?.currentRole || null;
  const availableRoles = roleContext?.availableRoles || [];
  const switchRole = roleContext?.switchRole;

  // Отслеживаем состояние WebSocket соединения
  useEffect(() => {
    if (!isAuthenticated) {
      setIsWebSocketConnected(false);
      return;
    }

    // Проверяем начальное состояние
    const checkConnection = () => {
      const connected = websocketNotificationService.isConnected();
      setIsWebSocketConnected(connected);
    };

    checkConnection();

    // Проверяем каждые 3 секунды
    const interval = setInterval(checkConnection, 3000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Load user's organizations
  useEffect(() => {
    if (!isAuthenticated) return;
    setOrgLoading(true);
    api
      .listOrganizations()
      .then((res) => {
        if (!res.error) {
          setOrgList(res.data);
          setOrgError(null);
        } else {
          setOrgError(res.error);
        }
      })
      .catch((err) => {
        setOrgError(err?.message || "Failed to load organizations");
      })
      .finally(() => setOrgLoading(false));
  }, [isAuthenticated]);

  const changeLanguage = useCallback(
    (lng: string) => {
      i18n.changeLanguage(lng);
    },
    [i18n],
  );

  const handleLogout = useCallback(async () => {
    await logout();
    // Полный сброс состояния роутера
    window.location.href = "/login";
  }, [logout]);

  const handleRoleChange = useCallback(
    async (event: any) => {
      if (!switchRole) return;
      const newRole = event.target.value;
      await switchRole(newRole);
      if (newRole === "dispatcher") {
        navigate("/fsm/board");
      } else if (newRole === "admin" || newRole === "super_admin") {
        navigate("/admin");
      } else {
        navigate("/home");
      }
    },
    [switchRole, navigate],
  );

  const handleEditOrg = useCallback(() => {
    const org = orgList.find((o) => o.id === selectedOrgId);
    if (org) {
      setEditOrgName(org.name);
      setEditOrgSlug(org.slug);
      setEditOrgOpen(true);
    }
  }, [selectedOrgId, orgList]);

  return (
    <AppBar
      position="static"
      color="default"
      sx={{
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
      }}
    >
      <Toolbar>
        <Box
          component="img"
          src="/logo_dp.webp"
          alt={t("title")}
          sx={{ height: 32, width: 32, mr: 1.5, display: { xs: 'none', sm: 'block' } }}
        />
        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 0.05,
            color: 'white',
            fontWeight: 700,
          }}
        >
          {t("title")}
        </Typography>
        
        {/* Live Indicator - показываем только для авторизованных */}
        {isAuthenticated && (
          <Box sx={{ ml: 2 }}>
            <LiveIndicator isConnected={isWebSocketConnected} size="small" />
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />
        
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Role selector - shown only when authenticated */}
          {isAuthenticated &&
            availableRoles &&
            availableRoles.length > 0 &&
            switchRole && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={currentRole || ""}
                  onChange={handleRoleChange}
                  sx={{
                    color: "white",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.5)",
                    },
                    "& .MuiSvgIcon-root": { color: "white" },
                  }}
                >
                  {availableRoles
                    .filter(
                      (role): role is NonNullable<typeof role> => role !== null,
                    )
                    .map((role) => (
                      <MenuItem key={role} value={role}>
                        {t(
                          role === "client"
                            ? "client"
                            : role === "performer"
                              ? "performer"
                              : role === "dispatcher"
                                ? "dispatcher"
                                : role === "admin"
                                  ? "admin"
                                  : "super_admin",
                        )}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

          {/* Organization selector */}
{isAuthenticated && (
  <FormControl size="small" sx={{ minWidth: 150, ml: 1 }}>
    <Select
      value={Number(localStorage.getItem("fsm_org_id") || 0) || ""}
      onChange={async (e) => {
        const orgId = Number(e.target.value);
        const res = await api.selectOrg(orgId);
        if (!res.error && res.data?.access_token) {
          localStorage.setItem("token", res.data.access_token);
          localStorage.setItem("refresh_token", res.data.refresh_token);
        }
        localStorage.setItem("fsm_org_id", String(orgId));
        window.location.reload();
      }}
      displayEmpty
      disabled={orgLoading}
      sx={{
        color: "white",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(255,255,255,0.5)",
        },
        "& .MuiSvgIcon-root": { color: "white" },
      }}
    >
      <MenuItem value="" disabled>{t("select_organization")}</MenuItem>
      {orgList.map((org) => (
        <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
      ))}
    </Select>
  </FormControl>
)}
<Button
  variant="text"
  color="inherit"
  startIcon={<AddIcon />}
  onClick={() => setCreateOrgOpen(true)}
  sx={{ ml: 1, textTransform: "none", color: "white" }}
>
  {t("create_organization")}
</Button>
{isAuthenticated && selectedOrgId && (
  <>
    <IconButton color="inherit" onClick={handleEditOrg} sx={{ ml: 1 }}>
      <EditIcon />
    </IconButton>
    <IconButton color="inherit" onClick={() => setDeleteConfirmOpen(true)} sx={{ ml: 1 }}>
      <DeleteIcon />
    </IconButton>
  </>
)}

{/* Language selector */}
<FormControl size="small" sx={{ minWidth: 120 }}>
  <Select
    value={i18n.language}
    onChange={(e) => changeLanguage(e.target.value)}
    sx={{
      color: "#1F2937",
      bgcolor: "#FFFFFF",
      borderRadius: "8px",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.15)" },
      "& .MuiSvgIcon-root": { color: "#1F2937" },
      "& .MuiSelect-select": { fontWeight: 600 },
    }}
  >
    <MenuItem value="ru">{t("russian")}</MenuItem>
    <MenuItem value="en">{t("english")}</MenuItem>
    <MenuItem value="kk">{t("kazakh")}</MenuItem>
    <MenuItem value="ky">{t("kyrgyz")}</MenuItem>
    <MenuItem value="tg">{t("tajik")}</MenuItem>
    <MenuItem value="uz">{t("uzbek")}</MenuItem>
  </Select>
</FormControl>

          {isAuthenticated ? (
            <>
              <Button
                color="inherit"
                component={Link}
                to="/home"
                sx={{
                  textTransform: "none",
                  "&:hover": {
                    color: "#FFD700", // gold color on hover
                    backgroundColor: "rgba(255, 255, 200, 0.2)",
                    transform: "scale(1.05)",
                    transition: "all 0.3s ease-in-out",
                    cursor: "pointer",
                  },
                  "&.active": {
                    backgroundColor: "#A3D4A9", // darker pale green for active state
                    borderRadius: "4px",
                  },
                  transition: "all 0.3s ease-in-out",
                  cursor: "pointer",
                }}
              >
                {t("home")}
              </Button>

              {currentRole === "dispatcher" ? (
                <Button
                  color="inherit"
                  component={Link}
                  to="/fsm/board"
                  sx={{
                    textTransform: "none",
                    "&:hover": {
                      color: "#FFD700",
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      transform: "scale(1.05)",
                      transition: "all 0.3s ease-in-out",
                      cursor: "pointer",
                    },
                    transition: "all 0.3s ease-in-out",
                    cursor: "pointer",
                  }}
                >
                  {t("fsm_board")}
                </Button>
              ) : currentRole === "admin" || currentRole === "super_admin" ? (
                <Button
                  color="inherit"
                  component={Link}
                  to="/admin"
                  sx={{
                    textTransform: "none",
                    "&:hover": {
                      color: "#FFD700",
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      transform: "scale(1.05)",
                      transition: "all 0.3s ease-in-out",
                      cursor: "pointer",
                    },
                    "&.active": {
                      backgroundColor: "#A3D4A9",
                      borderRadius: "4px",
                    },
                    transition: "all 0.3s ease-in-out",
                    cursor: "pointer",
                  }}
                >
                  {t("dashboard")}
                </Button>
              ) : (
                <Button
                  color="inherit"
                  component={Link}
                  to="/orders/new"
                  sx={{
                    textTransform: "none",
                    "&:hover": {
                      color: "#FFD700",
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      transform: "scale(1.05)",
                      transition: "all 0.3s ease-in-out",
                      cursor: "pointer",
                    },
                    "&.active": {
                      backgroundColor: "#A3D4A9",
                      borderRadius: "4px",
                    },
                    transition: "all 0.3s ease-in-out",
                    cursor: "pointer",
                  }}
                >
                  {t("new_order")}
                </Button>
              )}

              <Button
                color="inherit"
                component={Link}
                to="/settings"
                sx={{
                  textTransform: "none",
                  "&:hover": {
                    color: "#FFD700", // gold color on hover
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    transform: "scale(1.05)",
                    transition: "all 0.3s ease-in-out",
                    cursor: "pointer",
                  },
                  "&.active": {
                    backgroundColor: "#A3D4A9", // darker pale green for active state
                    borderRadius: "4px",
                  },
                  transition: "all 0.3s ease-in-out",
                  cursor: "pointer",
                }}
              >
                {t("settings")}
              </Button>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Tooltip title={user?.name || user?.email || t("profile")}>
                  <IconButton
                    color="inherit"
                    onClick={() => navigate("/profile")}
                    sx={{ padding: 0, cursor: "pointer" }}
                  >
                    <AccountCircleIcon sx={{ cursor: "pointer" }} />
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="body2"
                  color="inherit"
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate("/profile")}
                >
                  {user?.name || user?.email || t("user")}
                </Typography>

                {/* Notification Bell - only show for authenticated users */}
                {isAuthenticated && <NotificationBell />}

                <Tooltip title={t("help")}>
                  <IconButton
                    color="inherit"
                    component={Link}
                    to="/help"
                    sx={{
                      ml: 1,
                      "&:hover": {
                        color: "#FFD700",
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                      },
                    }}
                  >
                    <HelpOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Button
                color="inherit"
                onClick={handleLogout}
                sx={{
                  textTransform: "none",
                  "&:hover": {
                    color: "#FFD700", // gold color on hover
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    transform: "scale(1.05)",
                    transition: "all 0.3s ease-in-out",
                    cursor: "pointer",
                  },
                  "&.active": {
                    backgroundColor: "#A3D4A9", // darker pale green for active state
                    borderRadius: "4px",
                  },
                  transition: "all 0.3s ease-in-out",
                  cursor: "pointer",
                }}
              >
                {t("logout")}
              </Button>
            </>
          ) : (
            <>
              {isHelpDomain() && (
                <Button
                  color="inherit"
                  component={Link}
                  to="/for-brigades"
                  sx={{ textTransform: "none" }}
                >
                  {t("for_brigades")}
                </Button>
              )}
              <Button
                color="inherit"
                component={Link}
                to={isBrigadeDomain() ? "/" : "/landing"}
                sx={{ textTransform: "none" }}
              >
                {t("home")}
              </Button>
              <Button
                color="inherit"
                component={Link}
                to="/login"
                sx={{ textTransform: "none" }}
              >
                {t("login")}
              </Button>
            </>
          )}
        </Box>
      </Toolbar>
    {/* Create Organization Dialog */}
          <Dialog open={createOrgOpen} onClose={() => setCreateOrgOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>{t("create_organization")}</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                label={t("name")}
                value={newOrgName}
                onChange={e => setNewOrgName(e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label={t("slug")}
                value={newOrgSlug}
                onChange={e => setNewOrgSlug(e.target.value)}
                margin="normal"
                helperText={t("slugHelper")}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCreateOrgOpen(false)}>{t("cancel")}</Button>
              <Button
                variant="contained"
                onClick={async () => {
                  if (!newOrgName.trim()) return;
                  setCreatingOrg(true);
                  const slug = newOrgSlug.trim();
                  const res = await api.createOrganization({ name: newOrgName, slug });
                  if (!res.error && res.data) {
                    const created = res.data.organization || res.data;
                    const createdId = created?.id;
                    if (createdId) {
                      await api.selectOrg(createdId);
                      localStorage.setItem("fsm_org_id", String(createdId));
                      const listRes = await api.listOrganizations();
                      if (!listRes.error) setOrgList(listRes.data);
                    }
                  } else {
                    setOrgError(res.error || "Error creating organization");
                  }
                  setCreatingOrg(false);
                  setCreateOrgOpen(false);
                  setNewOrgName("");
                  setNewOrgSlug("");
                }}
                disabled={creatingOrg || !newOrgName.trim()}
              >
                {creatingOrg ? t("creating") : t("create")}
              </Button>
            </DialogActions>
          </Dialog>
          {/* Edit Organization Dialog */}
<Dialog open={editOrgOpen} onClose={() => setEditOrgOpen(false)} maxWidth="sm" fullWidth>
  <DialogTitle>{t("edit_organization")}</DialogTitle>
  <DialogContent>
    <TextField
      fullWidth
      label={t("name")}
      value={editOrgName}
      onChange={e => setEditOrgName(e.target.value)}
      margin="normal"
      required
    />
    <TextField
      fullWidth
      label={t("slug")}
      value={editOrgSlug}
      onChange={e => setEditOrgSlug(e.target.value)}
      margin="normal"
      helperText={t("slugHelper")}
    />
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setEditOrgOpen(false)}>{t("cancel")}</Button>
    <Button
      variant="contained"
      onClick={async () => {
        if (!editOrgName.trim()) return;
        setCreatingOrg(true);
        const res = await api.updateOrganization(selectedOrgId, { name: editOrgName, slug: editOrgSlug.trim() });
        if (!res.error && res.data) {
          const listRes = await api.listOrganizations();
          if (!listRes.error) setOrgList(listRes.data);
        } else {
          setOrgError(res.error || "Error updating organization");
        }
        setCreatingOrg(false);
        setEditOrgOpen(false);
      }}
      disabled={creatingOrg || !editOrgName.trim()}
    >
      {t("save")}
    </Button>
  </DialogActions>
</Dialog>

{/* Delete Organization Confirmation */}
<Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
  <DialogTitle>{t("delete_organization")}</DialogTitle>
  <DialogContent>
    <Typography>{t("organization_deleted")}</Typography>
    <Typography>{t("confirm_deletion")}</Typography>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setDeleteConfirmOpen(false)}>{t("cancel")}</Button>
    <Button
      color="error"
      onClick={async () => {
        setDeletingOrg(true);
        const res = await api.deleteOrganization(selectedOrgId);
        if (!res.error) {
          setOrgList(orgList.filter(o => o.id !== selectedOrgId));
          localStorage.removeItem("fsm_org_id");
        } else {
          setOrgError(res.error || "Error deleting organization");
        }
        setDeletingOrg(false);
        setDeleteConfirmOpen(false);
      }}
      disabled={deletingOrg}
    >
      {deletingOrg ? t("deleting") : t("delete")}
    </Button>
  </DialogActions>
</Dialog>

</AppBar>
  );
};

export default Header;
